import React, { useEffect, useState } from 'react';
import { ActivityIndicator, FlatList } from 'react-native';
import Colors from '../../constants/colors';
import type { AthleteProps, EventProps, ExternalPriceProps, MarketProps, MatchProps, TeamProps, TournamentProps, TradeProps } from '../../types';
import { MarketComponentApi, MarketComponentHelpers } from '../api';
import Toggle from '../../Components/Toggle';
import moment from 'moment-mini';
import { view_styles } from '../../constants/styles';
import { View, Text, Button } from '../../Components/Themed';

type ExternalPriceComparisonProps = {
    price_key:string,
    contest_id:string,
    style?:any
    contest_type:string,
    float?:boolean,
    market_id:string,
    maxHeight?:number,
    participant_type?:string,
    external_name?:string,
    participant_id?:string,
    init_side?:string,
    onClose:() => void,
    onSeePriceHistory?:(props: {
        contest_id: string,
        contest_type: string,
        market_id: string,
        participant_id?: string,
        participant_type?: string,
        selected_side: string,
        var_1?: number
    }) => void
}


const ExternalPriceComparison = ({ price_key, float, contest_id, style, contest_type, market_id, participant_id, participant_type, external_name, init_side, onClose, onSeePriceHistory }:ExternalPriceComparisonProps) => {
    const [ module_data, setModuleData ] = useState<{
        loading:boolean,
        market?:MarketProps,
        selected_side:string,
        athlete?:AthleteProps,
        team?:TeamProps,
        trades:TradeProps[],
        event?:EventProps,
        tournament?:TournamentProps,
        match?:MatchProps,
        external_prices:ExternalPriceProps[]
    }>({
        loading:false,
        selected_side: '',
        trades:[],
        external_prices: []
    });
    const { loading, event, athlete, team, market, selected_side, tournament, match, trades, external_prices } = module_data;

    useEffect(() => {
        MarketComponentApi.setEnvironment();
        if(!price_key || !contest_id){ return }
        getData()
    },[price_key]);

    const getData = async() => {
        setModuleData({ ...module_data, loading:true });
        const prices = await MarketComponentApi.getExternalPrices(contest_type, contest_id, market_id, undefined, participant_id, participant_type, external_name);
        const ts = await MarketComponentApi.getLatestTradesByEvents(contest_type, [contest_id]);
        let market_ts = ts.filter(lt => lt.market_id == market_id && lt.market_type == 'FOR_MONEY');
        let a:AthleteProps | undefined = undefined
        let tm:TeamProps | undefined = undefined
        if(participant_id){
            market_ts = market_ts.filter(lt => lt.side_id == participant_id);
            if(participant_type == 'athlete'){
                let as = await MarketComponentApi.getAthletesByIds([participant_id])
                a = as[0]
            }
            if(participant_type == 'team'){
                let tms = await MarketComponentApi.getTeamsByIds([participant_id])
                tm = tms[0]
            }
        }
        const markets = await MarketComponentApi.getMarkets();
        const mk = markets.find(m => m.market_id == market_id)
        
        let e:EventProps | undefined = undefined
        let t:TournamentProps | undefined = undefined
        let m:MatchProps | undefined = undefined

        if(contest_type == 'team'){ 
            let es = await MarketComponentApi.getEventsByEventIds([contest_id]) 
            e = es[0]
        }
        if(contest_type == 'tournament'){
            let ts = await MarketComponentApi.getTournamentsByTournamentIds([contest_id])
            t = ts[0]
        }
        if(contest_type == 'match'){
            let ms = await MarketComponentApi.getMatchesByMatchIds([contest_id])
            m = ms[0]
        }



        setModuleData({
            ...module_data,
            loading: false,
            athlete: a,
            team: tm,
            external_prices: prices,
            event: e,
            tournament:t,
            trades: market_ts,
            match: m,
            market:mk,
            selected_side: init_side ?? mk?.trade_side ?? ''
        })
    }

    const renderPrices = (data:{item:ExternalPriceProps, index:number}) => {
        if(!market){ return <></> }
        return (
            <View transparent type='row'>
                <View transparent style={{ flex:1 }}>
                    <Text theme='h2'>{data.item.external_name}</Text>
                    <Text style={{ marginTop:3 }} size={10} theme='light'>{(data.item.vig_pct*100).toFixed(2)}% Vig</Text>
                </View>
                <View transparent style={{ alignItems:'flex-end' }}>
                    <Text theme='h2'>{market.var_1_required ? `${MarketComponentHelpers.getVar1Label(market, data.item.var_1)} `:''}{MarketComponentHelpers.getOddsLabel(data.item.odds)}</Text>
                    <Text style={{ marginTop:3 }} size={10} theme='light'>As of: {moment(data.item.last_update_datetime).fromNow()}</Text>
                </View>
            </View>
        )
    }

    const { o_side_label, trade_side_label } = MarketComponentHelpers.getExternalPriceDetails(contest_type, market, event, tournament, match)
    let visible_prices = external_prices.filter(p => p.side == selected_side).sort((a,b) => a.external_name.localeCompare(b.external_name))
    const consensus_price = visible_prices.find(p => p.external_name.toLowerCase() == 'consensus');
    visible_prices = visible_prices.filter(p => p.external_name.toLowerCase() != 'consensus');

    const latest_trade = trades.find(t => t.side == selected_side)
    return (
        <View float={float} style={[style]}>
            <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                <View transparent style={{ flexGrow:1 }}>
                    <Text theme='h1'>External Price Comparison</Text>
                    {market ?
                    <Text theme='description' style={{ marginTop:3 }}>{athlete?`${athlete.abbr_name} `:team?`${team.abbr} `:''}{market.type == 'Stat' ? market.stat_label : market.type}</Text>
                    :<></>}
                </View>
                <View transparent style={{ flexDirection:'row', gap: 10 }}>
                    {onSeePriceHistory && (
                        <Button
                            title='See Price History'
                            type='success'
                            style={{ padding:10 }}
                            onPress={() => {
                                onSeePriceHistory({
                                    contest_id,
                                    contest_type,
                                    market_id,
                                    participant_id,
                                    participant_type,
                                    selected_side,
                                    var_1: latest_trade?.var_1
                                });
                            }}
                        />
                    )}
                    <Button
                        title='X'
                        style={{ padding:10 }}
                        type='close'
                        onPress={() => onClose()}
                    />
                </View>
            </View>
            <View transparent type='body' style={{ padding:20 }}>
                {loading ?
                <ActivityIndicator size='large' color={Colors.brand.midnight} style={{ padding:20, alignSelf:'center' }}/>
                :<></>}
                {market ?
                <Toggle
                    //background_color={Colors.shades.shade100}
                    options={[{ key: market.side_options.find(so => so.side != market.trade_side)?.side ?? '', label: o_side_label }, { key: market.trade_side, label: trade_side_label }]}
                    onSelectOption={(option) => setModuleData({ ...module_data, selected_side: option })}
                    selected_option={selected_side}
                />
                :<></>}
                
                <View transparent nativeID='header_trades' style={{ paddingTop:20, borderBottomWidth:1, borderColor:Colors.shades.shade600, paddingBottom:10 }}>
                    {latest_trade && market ?
                    <View transparent style={{ ...view_styles.body_row }}>
                        <View transparent style={{ flex:1 }}>
                            <Text theme='h1'>BettorEdge</Text>
                            <Text style={{ marginTop:3 }} size={10} theme='light'>{0}% Vig</Text>
                        </View>
                        <View transparent style={{ alignItems:'flex-end' }}>
                            <Text theme='h2'>{market.var_1_required ? `${MarketComponentHelpers.getVar1Label(market, latest_trade.var_1)} `:''}{MarketComponentHelpers.getOddsLabel(latest_trade.odds)}</Text>
                            <Text style={{ marginTop:3 }} size={10} theme='light'>Traded: {moment(latest_trade.last_update_datetime).fromNow()}</Text>
                        </View>
                    </View>
                    :<></>}
                    {consensus_price && market ?
                    <View transparent style={{ ...view_styles.body_row }}>
                        <View transparent style={{ flex:1 }}>
                            <Text theme='h2'>Consensus</Text>
                            <Text style={{ marginTop:3 }} size={10} theme='light'>{(consensus_price.vig_pct*100).toFixed(2)}% Vig</Text>
                        </View>
                        <View transparent style={{ alignItems:'flex-end' }}>
                            <Text theme='h2'>{market.var_1_required ? `${MarketComponentHelpers.getVar1Label(market, consensus_price.var_1)} `:''}{MarketComponentHelpers.getOddsLabel(consensus_price.odds)}</Text>
                            <Text style={{ marginTop:3 }} size={10} theme='light'>As of: {moment(consensus_price.last_update_datetime).fromNow()}</Text>
                        </View>
                    </View>
                    :<></>}
                </View>
                <View transparent style={{ marginTop:10 }}>
                    <FlatList
                        data={visible_prices}
                        renderItem={renderPrices}
                        keyExtractor={(item) => item.external_price_id.toString()}
                    />
                </View>
            </View>
        </View>
    )
}

export default ExternalPriceComparison